import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import {
  type AllowedDiagnosticDTCType,
  DiagnosticDTCType,
  isTwelveVoltBatterySacType,
  type TwelveVoltBatterySacType,
  toTwelveVoltBatterySacType,
} from '../models/asset-report.model';

export const SAC_TYPE_TRANSLATION_MAP: Record<string, string> = {
  SAC001:
    'asset:diagnosticMapping.twelve_volt_battery_sac_subtypes.shorted_cell',
  SAC002:
    'asset:diagnosticMapping.twelve_volt_battery_sac_subtypes.high_resistance_or_connection_issue',
  SAC003:
    'asset:diagnosticMapping.twelve_volt_battery_sac_subtypes.low_charge_state',
  SAC004:
    'asset:diagnosticMapping.twelve_volt_battery_sac_subtypes.starter_or_connection_issue',
  SAC005:
    'asset:diagnosticMapping.twelve_volt_battery_sac_subtypes.starter_battery_or_connection_issue',
};

export const SAC_TYPE_RECOMMENDATION_MAP: Record<string, string> = {
  SAC001:
    'asset:diagnosticMapping.twelve_volt_battery_sac_recommendations.shorted_cell',
  SAC002:
    'asset:diagnosticMapping.twelve_volt_battery_sac_recommendations.high_resistance_or_connection_issue',
  SAC003:
    'asset:diagnosticMapping.twelve_volt_battery_sac_recommendations.low_charge_state',
  SAC004:
    'asset:diagnosticMapping.twelve_volt_battery_sac_recommendations.starter_or_connection_issue',
  SAC005:
    'asset:diagnosticMapping.twelve_volt_battery_sac_recommendations.starter_battery_or_connection_issue',
};

/** Trailing SAC code of a subtype, e.g. `twelve_volt_battery_SAC001` -> `SAC001`. */
export const getTwelveVoltBatterySacCode = (
  diagnosticType?: string
): string => {
  if (!diagnosticType || !isTwelveVoltBatterySacType(diagnosticType)) {
    return '';
  }

  const parts = diagnosticType.split('_');
  return parts[parts.length - 1].toUpperCase();
};

/**
 * Required, not defensive: the details endpoint keys prognostics `dtc_SAC00N`, which is not a SAC
 * type, and names the SAC in `name` (`SAC001`). Dropping the fallback loses every 12-volt alert.
 */
export const resolveTwelveVoltSacType = (
  key?: string,
  name?: string
): TwelveVoltBatterySacType | undefined =>
  toTwelveVoltBatterySacType(key) ??
  (name
    ? toTwelveVoltBatterySacType(`twelve_volt_battery_${name}`)
    : undefined);

export const get12VoltBatterySACTypeTranslationKey = (
  diagnosticType?: DiagnosticDTCType | TwelveVoltBatterySacType
) => {
  const sacCode = getTwelveVoltBatterySacCode(diagnosticType);
  return sacCode ? translateText(SAC_TYPE_TRANSLATION_MAP[sacCode]) : '';
};

export const get12VoltBatterySACRecommendation = (
  diagnosticType?: DiagnosticDTCType | TwelveVoltBatterySacType
) => {
  const sacCode = getTwelveVoltBatterySacCode(diagnosticType);
  return sacCode ? translateText(SAC_TYPE_RECOMMENDATION_MAP[sacCode]) : '';
};

export const getDynamicDiagnosticLabelContent = (
  diagnosticType: AllowedDiagnosticDTCType,
  originalAPIDiagnosticType?: DiagnosticDTCType | TwelveVoltBatterySacType
) => {
  switch (diagnosticType) {
    case DiagnosticDTCType.TWELVE_VOLT_BATTERY_ISSUE: {
      const sacTypeTranslationKey = get12VoltBatterySACTypeTranslationKey(
        originalAPIDiagnosticType
      );
      return sacTypeTranslationKey
        ? { issue: sacTypeTranslationKey }
        : undefined;
    }
    default:
      return undefined;
  }
};

export const isTwelveVoltPrognostic = (
  diagnosticType: DiagnosticDTCType,
  originalAPIDiagnosticType?: DiagnosticDTCType | TwelveVoltBatterySacType
) => {
  return (
    diagnosticType === DiagnosticDTCType.TWELVE_VOLT_BATTERY &&
    !!originalAPIDiagnosticType &&
    isTwelveVoltBatterySacType(originalAPIDiagnosticType)
  );
};

/**
 * Which icon the dashboard draws for a diagnostic. Once prognostics are enabled every 12-volt
 * entry takes the prognostics glyph, including a low-battery DTC that absorbed prognostics,
 * because the design shows one 12-volt battery icon regardless of which alert produced it.
 *
 * The vehicle list is deliberately not a caller: its own CSS recolours the pre-prognostics
 * glyph to match the design there.
 */
export const getDiagnosticIconType = (
  type: AllowedDiagnosticDTCType,
  isTwelveVoltBatteryEnabled: boolean
): AllowedDiagnosticDTCType =>
  isTwelveVoltBatteryEnabled && type === DiagnosticDTCType.TWELVE_VOLT_BATTERY
    ? DiagnosticDTCType.TWELVE_VOLT_BATTERY_ISSUE
    : type;

/**
 * Severity runs SAC001, SAC002, SAC005, SAC004, SAC003 — not numeric order, so SAC005
 * outranks both SAC004 and SAC003. Consulted only when the backend omits `severityRank`.
 */
const SAC_SEVERITY_RANK_BY_CODE: Record<string, number> = {
  SAC001: 1,
  SAC002: 2,
  SAC005: 3,
  SAC004: 4,
  SAC003: 5,
};

/**
 * The backend's rank wins whenever it sends one; a subtype that is neither ranked nor
 * recognized sorts last, so it can never outrank a subtype whose severity is known.
 */
export const getTwelveVoltSeverityRank = (subtype: {
  severityRank?: number;
  sacType?: string;
  type?: string;
}): number =>
  subtype.severityRank ??
  SAC_SEVERITY_RANK_BY_CODE[
    getTwelveVoltBatterySacCode(subtype.sacType ?? subtype.type)
  ] ??
  Number.MAX_SAFE_INTEGER;

/**
 * Only one 12-volt alert is ever surfaced: the most severe critical subtype (lower rank
 * wins). Non-critical subtypes are never shown.
 */
export const pickMostSevereTwelveVoltSac = <
  T extends { severityRank?: number; sacType?: string; type?: string },
>(
  criticalSubtypes: T[]
): T | undefined =>
  [...criticalSubtypes].sort(
    (a, b) => getTwelveVoltSeverityRank(a) - getTwelveVoltSeverityRank(b)
  )[0];
